import { Navigate, Outlet, useLocation } from 'react-router-dom'

import type { UserType } from '@/constants/roles'
import { useAuthStore } from '@/store/authStore'

interface RoleGuardProps {
  allowedRoles?: readonly UserType[]
}

/**
 * Route-level gate mirroring the backend's auth:sanctum + account.active +
 * role middleware stack. This is defense in depth for UX only — the API
 * enforces the real authorization; this just avoids flashing protected UI
 * before a redirect.
 */
export function RoleGuard({ allowedRoles }: RoleGuardProps) {
  const { user, status } = useAuthStore()
  const location = useLocation()

  if (status === 'idle') {
    return null
  }

  if (status === 'unauthenticated' || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (allowedRoles && !allowedRoles.includes(user.userType)) {
    return <Navigate to="/" replace />
  }

  // Mirrors the backend's `two-factor.enrolled` middleware (recruiter_admin/
  // super_admin can't reach any gated route until MFA is set up) — avoids
  // flashing protected UI before every one of those requests 403s.
  if (
    user.requiresTwoFactor &&
    !user.twoFactorEnabled &&
    location.pathname !== '/security/two-factor'
  ) {
    return <Navigate to="/security/two-factor" replace />
  }

  return <Outlet />
}
